import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { List, X, CaretDown, User, SignOut, Gauge } from '@phosphor-icons/react';

const NAV_ITEMS = [
  { label: 'Accueil', path: '/' },
  { label: 'Presentation', path: '/presentation' },
  { label: 'Services VTC', path: '/service-vtc' },
  { label: 'Avis', path: '/avis' },
  { label: 'Contact', path: '/contact' },
];

export default function Header() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#0d0d0d]/90 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <span className="font-heading text-2xl text-white tracking-tight">
              Desti<span className="text-desti-gold">Driving</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'home'}`}
                className={`text-sm font-body tracking-wide transition-colors duration-300 ${
                  location.pathname === item.path
                    ? 'text-desti-gold'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <div className="relative">
                <button
                  data-testid="user-menu-btn"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 text-sm text-white/80 hover:text-white transition-colors"
                >
                  <User size={18} weight="light" />
                  <span>{user.firstName}</span>
                  <CaretDown size={14} className={`transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 mt-3 w-56 bg-desti-surface border border-white/10 shadow-2xl" data-testid="user-dropdown">
                    {user.role === 'ADMIN' && (
                      <Link to="/admin/dashboard" className="flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-testid="admin-link">
                        <Gauge size={16} /> Administration
                      </Link>
                    )}
                    <Link to="/espace-membre/dashboard" className="flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors" data-testid="member-dashboard-link">
                      <User size={16} /> Mon espace
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white/80 hover:text-white hover:bg-white/5 transition-colors border-t border-white/5"
                      data-testid="logout-btn"
                    >
                      <SignOut size={16} /> Deconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/connexion" className="text-sm text-white/80 hover:text-white transition-colors" data-testid="login-link">
                  Connexion
                </Link>
                <Link
                  to="/#booking"
                  onClick={(e) => { e.preventDefault(); navigate('/'); setTimeout(() => document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' }), 100); }}
                  className="bg-desti-gold text-desti-black font-medium text-sm tracking-wide px-6 py-2.5 hover:bg-desti-gold-hover transition-colors"
                  data-testid="cta-reserve-header"
                >
                  Reserver
                </Link>
              </>
            )}
          </div>

          <button
            className="lg:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-desti-black/95 backdrop-blur-xl border-t border-white/10" data-testid="mobile-menu">
          <div className="px-6 py-6 space-y-4">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`block text-lg font-body ${location.pathname === item.path ? 'text-desti-gold' : 'text-white/70'}`}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/10 space-y-3">
              {user ? (
                <>
                  <Link to="/espace-membre/dashboard" className="block text-white/70">Mon espace</Link>
                  {user.role === 'ADMIN' && <Link to="/admin/dashboard" className="block text-white/70">Administration</Link>}
                  <button onClick={handleLogout} className="text-white/70">Deconnexion</button>
                </>
              ) : (
                <>
                  <Link to="/connexion" className="block text-white/70">Connexion</Link>
                  <Link to="/inscription" className="block text-desti-gold">Inscription</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
